import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useSubscriptionPlans } from "@/hooks/use-subscription";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { BarChart3, Check, Loader2, CreditCard } from "lucide-react";
// We're not using useAuth here as this component is used during signup before auth is established
import { SubscriptionModal } from "@/components/ui/subscription-modal";
import { toast } from "@/hooks/use-toast";

const planSelectionSchema = z.object({
  subscriptionPlanId: z.coerce.number().min(0, "Please select a subscription plan"),
  businessName: z.string().min(2, { message: "Business name must be at least 2 characters" }),
});

type PlanSelectionFormValues = z.infer<typeof planSelectionSchema>;

export default function PlanSelectionPage() {
  const [, navigate] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // During signup flow, we don't have access to useAuth yet, so we'll handle registration differently
  const registerMutation = {
    mutate: async (data: any) => {
      try {
        const res = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Registration failed');
        }
        
        const userData = await res.json();
        return userData;
      } catch (err: any) {
        toast({
          title: 'Registration failed',
          description: err.message,
          variant: 'destructive',
        });
        throw err;
      }
    }
  };
  const { data: plans, isLoading: isLoadingPlans } = useSubscriptionPlans();
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  
  // Get registration data from location state or session storage
  useEffect(() => {
    const storedData = sessionStorage.getItem("registrationData");
    if (storedData) {
      try {
        setRegistrationData(JSON.parse(storedData));
      } catch (e) {
        navigate("/signup");
      }
    } else {
      navigate("/signup");
    }
  }, [navigate]);
  
  // Check for query params from returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscription = params.get('subscription');
    
    if (subscription === 'success') {
      toast({
        title: "Subscription successful!",
        description: "Your subscription has been set up successfully.",
      });
    } else if (subscription === 'cancelled') {
      toast({
        title: "Subscription cancelled",
        description: "You can choose a different plan or try again later.",
        variant: "destructive"
      });
    }
  }, []);

  const form = useForm<PlanSelectionFormValues>({
    resolver: zodResolver(planSelectionSchema),
    defaultValues: {
      subscriptionPlanId: 0,
      businessName: "",
    },
  });

  const onSubmit = async (values: PlanSelectionFormValues) => {
    if (!registrationData) return;

    setIsSubmitting(true);
    try {
      // Double-check email exists before registration
      const response = await fetch('/api/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registrationData.email })
      });

      const data = await response.json();

      // If email already exists, show error and redirect back to signup
      if (data.exists) {
        toast({
          title: 'Email already registered',
          description: 'This email address is already in use. Please use a different email address.',
          variant: 'destructive',
        });
        navigate('/auth/signup');
        return;
      }

      // Complete registration with plan selection
      const completeRegistrationData = {
        ...registrationData,
        ...values,
        isTrial: values.subscriptionPlanId === 0,
        trialStartedAt: values.subscriptionPlanId === 0 ? new Date().toISOString() : null,
        trialEndedAt: values.subscriptionPlanId === 0 ?
          new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() : null,
      };

      // Submit complete registration data
      await registerMutation.mutate(completeRegistrationData);

      // Clear stored registration data
      sessionStorage.removeItem("registrationData");
      // Redirect to dashboard
      navigate("/dashboard");

      toast({
        title: 'Registration successful',
        description: values.subscriptionPlanId === 0
          ? 'Welcome to ZentroBooks! Your 14-day free trial has started.'
          : 'Welcome to ZentroBooks! You are now registered.',
      });
    } catch (error: any) {
      console.error('Error during registration:', error);
      toast({
        title: 'Registration failed',
        description: error.message || 'Unable to complete registration. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleOpenSubscriptionModal = () => {
    const selectedPlanId = Number(form.getValues("subscriptionPlanId"));
    
    // If free trial is selected, no need for payment
    if (selectedPlanId === 0) {
      toast({
        title: "Free Trial Selected",
        description: "No payment is required for the free trial. Simply click 'Complete Registration' to start your trial.",
      });
      return;
    }
    
    if (!selectedPlanId) {
      toast({
        title: "Please select a plan",
        description: "You need to select a subscription plan before proceeding to checkout.",
        variant: "destructive"
      });
      return;
    }
    setSubscriptionModalOpen(true);
  };

  if (isLoadingPlans || !registrationData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      {/* Subscription Modal */}
      <SubscriptionModal 
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
      />
      
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-primary flex items-center justify-center rounded-md text-primary-foreground">
              <BarChart3 className="h-7 w-7" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">Choose Your Subscription Plan</h1>
          <p className="text-neutral-500 mt-2">
            Select a plan for your business and start managing your finances today.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="businessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg">Business Name</FormLabel>
                  <FormDescription>
                    Enter the name of your business
                  </FormDescription>
                  <FormControl>
                    <Input placeholder="Your Business Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subscriptionPlanId"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-lg">Subscription Plan</FormLabel>
                  <FormDescription>
                    Choose the plan that best fits your business needs
                  </FormDescription>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value.toString()}
                      className="grid grid-cols-1 md:grid-cols-3 gap-8"
                    >
                      {/* Free Trial Option */}
                      <div className="relative">
                        <RadioGroupItem
                          value="0"
                          id="plan-free-trial"
                          className="sr-only"
                        />
                        <label
                          htmlFor="plan-free-trial"
                          className={`
                            cursor-pointer flex flex-col h-full p-6 rounded-lg border-2 transition-all relative
                            ${Number(field.value) === 0
                              ? "border-primary bg-primary/5 shadow-md"
                              : "border-border hover:border-primary/30 hover:bg-primary/5"
                            }
                          `}
                        >
                          {Number(field.value) === 0 && (
                            <span className="absolute top-3 right-3 h-6 w-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                              <Check className="h-4 w-4" />
                            </span>
                          )}
                          <span className="absolute -top-3 left-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                            RECOMMENDED
                          </span>
                          <CardTitle className="text-xl">Free Trial</CardTitle>
                          <div className="mt-2 text-2xl font-bold">$0.00<span className="text-sm font-normal text-neutral-500">/14 days</span></div>
                          <CardDescription className="mt-2 flex-grow">Try ZentroBooks Premium for 14 days risk-free</CardDescription>
                          <ul className="mt-4 space-y-2">
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-primary mr-2" />
                              <span className="text-sm">Full access to all features</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-primary mr-2" />
                              <span className="text-sm">Import up to 25 transactions</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-primary mr-2" />
                              <span className="text-sm">1 bank connection</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-primary mr-2" />
                              <span className="text-sm">15 AI categorizations</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-primary mr-2" />
                              <span className="text-sm">No credit card required</span>
                            </li>
                          </ul>
                        </label>
                      </div>
                      
                      {plans?.map((plan) => (
                        <div key={plan.id} className="relative">
                          <RadioGroupItem
                            value={plan.id.toString()}
                            id={`plan-${plan.id}`}
                            className="sr-only"
                          />
                          <label
                            htmlFor={`plan-${plan.id}`}
                            className={`
                              cursor-pointer flex flex-col h-full p-6 rounded-lg border-2 transition-all
                              ${Number(field.value) === plan.id
                                ? "border-primary bg-primary/5 shadow-md"
                                : "border-border hover:border-primary/30 hover:bg-primary/5"
                              }
                            `}
                          >
                            {Number(field.value) === plan.id && (
                              <span className="absolute top-3 right-3 h-6 w-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                                <Check className="h-4 w-4" />
                              </span>
                            )}
                            <CardTitle className="text-xl">{plan.name}</CardTitle>
                            <div className="mt-2 text-2xl font-bold">${Number(plan.monthlyPrice ?? plan.price ?? 0).toFixed(0)}<span className="text-sm font-normal text-neutral-500">/month</span></div>
                            <CardDescription className="mt-2 flex-grow">{plan.description}</CardDescription>
                            <ul className="mt-4 space-y-2">
                              {plan.features?.map((feature, i) => (
                                <li key={i} className="flex items-center">
                                  <Check className="h-4 w-4 text-primary mr-2" />
                                  <span className="text-sm">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                type="button" 
                variant="outline"
                size="lg" 
                className="flex-1"
                onClick={handleOpenSubscriptionModal}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Checkout with Stripe
              </Button>
              
              <Button
                type="submit"
                size="lg"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : "Complete Registration"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
